/*
 * TVG-147: AI Sales Assistant (Sales AI POC).
 *
 * Static proof-of-concept data, hard coded straight into the page per the
 * issue scope: no backend, no API, no data file fetch, no calculation. Each
 * lead maps to one ranked row; the meta/analysis/venue values drive the page
 * chrome around the list.
 */

/** Colour class for a signal tag. First item of each signal pair. */
export type SignalClass = 'value' | 'amber' | 'blue' | 'green' | 'red' | 'grey';

/** A signal tag: [colour class, label]. */
export type Signal = [SignalClass, string];

/** A label/value pair used by the gap line and the quote breakdown. */
export type Pair = [string, string];

export interface Lead {
  id: number;
  rank: number;
  name: string;
  venue: string;
  date: string;
  score: number;
  band: 'hot' | 'warm';
  bandLabel: string;
  total: string;
  highValue: boolean;
  fitClass: string;
  fitGrade: string;
  reason: string;
  signals: Signal[];
  profile: string;
  gapLeft: Pair;
  gapRight: Pair;
  fitNote: string;
  why: string[];
  action: string;
  draftSubj: string;
  draft: string;
  quote: Pair[];
}

export interface BoardMeta {
  title: string;
  subtitle: string;
  quotesProcessed: number;
  removed: number;
  venues: number;
  highValueProspects: number;
  prospects: number;
  topToCallFirst: number;
  lastRun: string;
}

export interface BoardData {
  meta: BoardMeta;
  analysisComments: string[];
  venueFilter: string[];
  leads: Lead[];
}

export const AI_SALES_ASSISTANT_BOARD: BoardData = {
  meta: {
    title: 'AI Sales Assistant',
    subtitle: 'Hot enquiries, ranked by who to call first',
    quotesProcessed: 3237,
    removed: 92,
    venues: 5,
    highValueProspects: 422,
    prospects: 2609,
    topToCallFirst: 10,
    lastRun: '10 June 2026 export',
  },
  analysisComments: [
    '3,237 quotes processed, 92 test and internal entries removed, leaving 2,609 real prospects.',
    'Budget band is recorded on only 15% of quotes, so most couples are ranked on value, recency and repeat visits. Worth making budget mandatory on the website.',
    'No phone numbers are captured, so staff cannot dial from here. Worth adding to the quote form.',
    '422 prospects are high value at £15,000 or more, across five venues.',
  ],
  venueFilter: [
    'All venues',
    'Milling Barn',
    'The Great Barn',
    'Ashton Lodge Country House',
    'Plum Park Manor',
    'Redhouse Barn',
  ],
  leads: [
    {
      id: 1,
      rank: 1,
      name: 'Nellie Sudworth',
      venue: 'Redhouse Barn',
      date: '3 date options',
      score: 63,
      band: 'hot',
      bandLabel: 'Hot',
      total: '£22,710',
      highValue: true,
      fitClass: 'top',
      fitGrade: 'Top bracket, no ceiling',
      reason: 'Estimated total £22,710, well above the £15k high value line; 2 quotes so far',
      signals: [
        ['value', '★ High value'],
        ['blue', 'Top bracket'],
        ['blue', 'Returning x2'],
        ['amber', 'Peak Saturday'],
      ],
      profile:
        'A £22,710 enquiry at Redhouse Barn with 3 dates in play. Back 2 times, most recent 3 days ago. They are in the open top budget band, so there is no ceiling and they are judged on value. Numbers are 150 day and 150 evening.',
      gapLeft: ['Stated budget', '10,250+ GBP'],
      gapRight: ['Estimated total', '£22,710'],
      fitNote:
        'They are in the open top budget band, so there is no ceiling and they are judged on value.',
      why: [
        'Estimated total £22,710, well above the £15k high value line',
        'Last active 3 days ago, so fresh',
        '2 quotes so far',
        '3 dates still open, so undecided',
      ],
      action: 'Call today, hold the options, and book a viewing this week.',
      draftSubj: 'Your July 2027 wedding at Redhouse Barn',
      draft:
        'Hi Nellie,\n\nThank you for building your quote with us. Your July 2027 wedding would be lovely at Redhouse Barn, and the dates you are considering are open.\n\nThe best next step is usually to come and see the space. I have viewing slots this week if any suit.\n\nWarm regards,\nThe Redhouse Barn team',
      quote: [
        ['Venue', 'Redhouse Barn'],
        ['Date', '3 date options'],
        ['Breakfast menu', 'The Casual'],
        ['Drinks', 'House'],
        ['Evening food', 'Cheese Board'],
        ['Day guests', '150'],
        ['Evening guests', '150'],
        ['Quotes run', '2'],
        ['First seen', '9 days ago'],
        ['Last active', '3 days ago'],
      ],
    },
    {
      id: 2,
      rank: 2,
      name: 'Nicole Macdonald',
      venue: 'Plum Park Manor',
      date: '6 date options',
      score: 60,
      band: 'hot',
      bandLabel: 'Hot',
      total: '£19,840',
      highValue: true,
      fitClass: 'top',
      fitGrade: 'Top bracket, no ceiling',
      reason: 'Estimated total £19,840, well above the £15k high value line; 5 quotes so far',
      signals: [
        ['value', '★ High value'],
        ['blue', 'Top bracket'],
        ['blue', 'Returning x5'],
        ['amber', 'Peak Saturday'],
      ],
      profile:
        'A £19,840 enquiry at Plum Park Manor with 6 dates in play. Back 5 times, most recent 12 days ago. They are in the open top budget band, so there is no ceiling and they are judged on value. Numbers are 120 day and 200 evening.',
      gapLeft: ['Stated budget', '10,250+ GBP'],
      gapRight: ['Estimated total', '£19,840'],
      fitNote:
        'They are in the open top budget band, so there is no ceiling and they are judged on value.',
      why: [
        'Estimated total £19,840, well above the £15k high value line',
        'Last active 12 days ago',
        '5 quotes so far',
        '6 dates still open, so undecided',
      ],
      action: 'Call today, hold the options, and book a viewing this week.',
      draftSubj: 'Your July 2027 wedding at Plum Park Manor',
      draft:
        'Hi Nicole,\n\nThank you for building your quote with us. Your July 2027 wedding would be lovely at Plum Park Manor, and the dates you are considering are open.\n\nThe best next step is usually to come and see the space. I have viewing slots this week if any suit.\n\nWarm regards,\nThe Plum Park Manor team',
      quote: [
        ['Venue', 'Plum Park Manor'],
        ['Date', '6 date options'],
        ['Breakfast menu', 'The Formal'],
        ['Drinks', 'Flexi Lux'],
        ['Evening food', 'Wood Fired Pizzas from the Shack'],
        ['Day guests', '120'],
        ['Evening guests', '200'],
        ['Quotes run', '5'],
        ['First seen', '79 days ago'],
        ['Last active', '12 days ago'],
      ],
    },
    {
      id: 3,
      rank: 3,
      name: 'Becky Chick',
      venue: 'Milling Barn',
      date: '3 date options',
      score: 59,
      band: 'hot',
      bandLabel: 'Hot',
      total: '£25,966',
      highValue: true,
      fitClass: 'top',
      fitGrade: 'Top bracket, no ceiling',
      reason: 'Estimated total £25,966, well above the £15k high value line; one quote so far, so reach out early',
      signals: [
        ['value', '★ High value'],
        ['blue', 'Top bracket'],
        ['grey', 'First quote'],
        ['amber', 'Peak Saturday'],
      ],
      profile:
        'A £25,966 enquiry at Milling Barn with 3 dates in play. Their first quote, 7 days ago. They are in the open top budget band, so there is no ceiling and they are judged on value. Numbers are 145 day and 250 evening.',
      gapLeft: ['Stated budget', '11,000+ GBP'],
      gapRight: ['Estimated total', '£25,966'],
      fitNote:
        'They are in the open top budget band, so there is no ceiling and they are judged on value.',
      why: [
        'Estimated total £25,966, well above the £15k high value line',
        'Last active 7 days ago, so fresh',
        'One quote so far, so reach out early',
        '3 dates still open, so undecided',
      ],
      action: 'Call today, hold the options, and book a viewing this week.',
      draftSubj: 'Your August 2028 wedding at Milling Barn',
      draft:
        'Hi Becky,\n\nThank you for building your quote with us. Your August 2028 wedding would be lovely at Milling Barn, and the dates you are considering are open.\n\nThe best next step is usually to come and see the space. I have viewing slots this week if any suit.\n\nWarm regards,\nThe Milling Barn team',
      quote: [
        ['Venue', 'Milling Barn'],
        ['Date', '3 date options'],
        ['Breakfast menu', 'The Casual'],
        ['Drinks', 'Flexi Lux'],
        ['Evening food', 'Sliced Flat Iron Steak'],
        ['Day guests', '145'],
        ['Evening guests', '250'],
        ['Quotes run', '1'],
        ['First seen', '7 days ago'],
        ['Last active', '7 days ago'],
      ],
    },
    {
      id: 4,
      rank: 4,
      name: 'Charlotte Evans',
      venue: 'Milling Barn',
      date: '5 date options',
      score: 58,
      band: 'hot',
      bandLabel: 'Hot',
      total: '£19,321',
      highValue: true,
      fitClass: 'top',
      fitGrade: 'Top bracket, no ceiling',
      reason: 'Estimated total £19,321, well above the £15k high value line; 3 quotes so far',
      signals: [
        ['value', '★ High value'],
        ['blue', 'Top bracket'],
        ['blue', 'Returning x3'],
        ['amber', 'Peak Saturday'],
      ],
      profile:
        'A £19,321 enquiry at Milling Barn with 5 dates in play. Back 3 times, most recent 5 days ago. They are in the open top budget band, so there is no ceiling and they are judged on value. Numbers are 100 day and 100 evening.',
      gapLeft: ['Stated budget', '10,000+ GBP'],
      gapRight: ['Estimated total', '£19,321'],
      fitNote:
        'They are in the open top budget band, so there is no ceiling and they are judged on value.',
      why: [
        'Estimated total £19,321, well above the £15k high value line',
        'Last active 5 days ago, so fresh',
        '3 quotes so far',
        '5 dates still open, so undecided',
      ],
      action: 'Call today, hold the options, and book a viewing this week.',
      draftSubj: 'Your April 2028 wedding at Milling Barn',
      draft:
        'Hi Charlotte,\n\nThank you for building your quote with us. Your April 2028 wedding would be lovely at Milling Barn, and the dates you are considering are open.\n\nThe best next step is usually to come and see the space. I have viewing slots this week if any suit.\n\nWarm regards,\nThe Milling Barn team',
      quote: [
        ['Venue', 'Milling Barn'],
        ['Date', '5 date options'],
        ['Breakfast menu', 'The Roast'],
        ['Drinks', 'Deluxe'],
        ['Evening food', 'Smokey Bacon Roll'],
        ['Day guests', '100'],
        ['Evening guests', '100'],
        ['Quotes run', '3'],
        ['First seen', '86 days ago'],
        ['Last active', '5 days ago'],
      ],
    },
    {
      id: 5,
      rank: 5,
      name: 'Paige Joseph',
      venue: 'Milling Barn',
      date: '4 date options',
      score: 58,
      band: 'warm',
      bandLabel: 'Warm',
      total: '£13,020',
      highValue: false,
      fitClass: 'over',
      fitGrade: 'Well over budget',
      reason: 'Estimated total £13,020, below the £15k high value line; 4 quotes so far',
      signals: [
        ['amber', 'Well over budget'],
        ['blue', 'Returning x4'],
        ['amber', 'Peak Saturday'],
        ['amber', '4 dates open'],
      ],
      profile:
        'A £13,020 enquiry at Milling Barn with 4 dates in play. Back 4 times, most recent 3 days ago. They are well over budget, which is the thing to manage on the call. Numbers are 80 day and 120 evening.',
      gapLeft: ['Stated budget', 'Under 5,250 GBP'],
      gapRight: ['Estimated total', '£13,020'],
      fitNote:
        'They are well over budget, which is the thing to manage on the call.',
      why: [
        'Estimated total £13,020, below the £15k high value line',
        'Last active 3 days ago, so fresh',
        '4 quotes so far',
        '4 dates still open, so undecided',
      ],
      action: 'Call this week. Engaged but over their stated budget, so steer the options to bring it into range.',
      draftSubj: 'Your July 2028 wedding at Milling Barn',
      draft:
        'Hi Paige,\n\nThank you for building your quote with us. Your July 2028 wedding would be lovely at Milling Barn, and the dates you are considering are open.\n\nThe best next step is usually to come and see the space. I have viewing slots this week if any suit.\n\nWarm regards,\nThe Milling Barn team',
      quote: [
        ['Venue', 'Milling Barn'],
        ['Date', '4 date options'],
        ['Breakfast menu', 'The Feast'],
        ['Drinks', 'Flexi Lux'],
        ['Evening food', 'Wood Fired Pizzas from the Shack'],
        ['Day guests', '80'],
        ['Evening guests', '120'],
        ['Quotes run', '4'],
        ['First seen', '102 days ago'],
        ['Last active', '3 days ago'],
      ],
    },
    {
      id: 6,
      rank: 6,
      name: 'Ash',
      venue: 'Ashton Lodge Country House',
      date: '2 date options',
      score: 57,
      band: 'hot',
      bandLabel: 'Hot',
      total: '£21,255',
      highValue: true,
      fitClass: 'unknown',
      fitGrade: 'Budget not given',
      reason: 'Estimated total £21,255, well above the £15k high value line; one quote so far, so reach out early',
      signals: [
        ['value', '★ High value'],
        ['grey', 'First quote'],
        ['grey', 'Landed today'],
      ],
      profile:
        'A £21,255 enquiry at Ashton Lodge Country House with 2 dates in play. Their first quote, landed today. No budget band was recorded, so they are ranked on value, recency and repeat visits. Numbers are 150 day and 250 evening.',
      gapLeft: ['Stated budget', 'Not given'],
      gapRight: ['Estimated total', '£21,255'],
      fitNote:
        'No budget band was recorded, so they are ranked on value, recency and repeat visits.',
      why: [
        'Estimated total £21,255, well above the £15k high value line',
        'Landed today',
        'One quote so far, so reach out early',
        '2 dates still open, so undecided',
      ],
      action: 'Call today, hold the options, and book a viewing this week.',
      draftSubj: 'Your August 2026 wedding at Ashton Lodge Country House',
      draft:
        'Hi Ash,\n\nThank you for building your quote with us. Your August 2026 wedding would be lovely at Ashton Lodge Country House, and the dates you are considering are open.\n\nThe best next step is usually to come and see the space. I have viewing slots this week if any suit.\n\nWarm regards,\nThe Ashton Lodge Country House team',
      quote: [
        ['Venue', 'Ashton Lodge Country House'],
        ['Date', '2 date options'],
        ['Breakfast menu', 'The Formal'],
        ['Drinks', 'Indulgence'],
        ['Evening food', 'Wood Fired Pizzas from the Shack'],
        ['Day guests', '150'],
        ['Evening guests', '250'],
        ['Quotes run', '1'],
        ['First seen', 'today'],
        ['Last active', 'today'],
      ],
    },
    {
      id: 7,
      rank: 7,
      name: 'Sophie',
      venue: 'The Great Barn',
      date: '6 date options',
      score: 57,
      band: 'hot',
      bandLabel: 'Hot',
      total: '£17,917',
      highValue: true,
      fitClass: 'over',
      fitGrade: 'Well over budget',
      reason: 'Estimated total £17,917, well above the £15k high value line; 2 quotes so far',
      signals: [
        ['value', '★ High value'],
        ['amber', 'Well over budget'],
        ['blue', 'Returning x2'],
        ['amber', 'Peak Saturday'],
      ],
      profile:
        'A £17,917 enquiry at The Great Barn with 6 dates in play. Back 2 times, most recent 3 days ago. They are well over budget, which is the thing to manage on the call. Numbers are 100 day and 100 evening.',
      gapLeft: ['Stated budget', '8,000 - 11,000 GBP'],
      gapRight: ['Estimated total', '£17,917'],
      fitNote:
        'They are well over budget, which is the thing to manage on the call.',
      why: [
        'Estimated total £17,917, well above the £15k high value line',
        'Last active 3 days ago, so fresh',
        '2 quotes so far',
        '6 dates still open, so undecided',
      ],
      action: 'Call today, hold the options, and book a viewing this week.',
      draftSubj: 'Your May 2028 wedding at The Great Barn',
      draft:
        'Hi Sophie,\n\nThank you for building your quote with us. Your May 2028 wedding would be lovely at The Great Barn, and the dates you are considering are open.\n\nThe best next step is usually to come and see the space. I have viewing slots this week if any suit.\n\nWarm regards,\nThe Great Barn team',
      quote: [
        ['Venue', 'The Great Barn'],
        ['Date', '6 date options'],
        ['Breakfast menu', 'The Feast'],
        ['Drinks', 'Indulgence'],
        ['Evening food', 'Smokey Bacon Roll'],
        ['Day guests', '100'],
        ['Evening guests', '100'],
        ['Quotes run', '2'],
        ['First seen', '31 days ago'],
        ['Last active', '3 days ago'],
      ],
    },
    {
      id: 8,
      rank: 8,
      name: 'Lauren Savage',
      venue: 'Milling Barn',
      date: 'Sat 7 Aug 2027',
      score: 56,
      band: 'hot',
      bandLabel: 'Hot',
      total: '£23,806',
      highValue: true,
      fitClass: 'unknown',
      fitGrade: 'Budget not given',
      reason: 'Estimated total £23,806, well above the £15k high value line; one quote so far, so reach out early',
      signals: [
        ['value', '★ High value'],
        ['grey', 'First quote'],
        ['amber', 'Peak Saturday'],
      ],
      profile:
        'A £23,806 enquiry at Milling Barn with a firm date of Sat 7 Aug 2027. Their first quote, 5 days ago. No budget band was recorded, so they are ranked on value, recency and repeat visits. Numbers are 158 day and 188 evening.',
      gapLeft: ['Stated budget', 'Not given'],
      gapRight: ['Estimated total', '£23,806'],
      fitNote:
        'No budget band was recorded, so they are ranked on value, recency and repeat visits.',
      why: [
        'Estimated total £23,806, well above the £15k high value line',
        'Last active 5 days ago, so fresh',
        'One quote so far, so reach out early',
        'A firm single date',
      ],
      action: 'Call today, hold the date, and book a viewing this week.',
      draftSubj: 'Your August 2027 wedding at Milling Barn',
      draft:
        'Hi Lauren,\n\nThank you for building your quote with us. Your August 2027 wedding would be lovely at Milling Barn, and the date is still open.\n\nThe best next step is usually to come and see the space. I have viewing slots this week if any suit.\n\nWarm regards,\nThe Milling Barn team',
      quote: [
        ['Venue', 'Milling Barn'],
        ['Date', 'Sat 7 Aug 2027'],
        ['Breakfast menu', 'The Formal'],
        ['Drinks', 'Indulgence'],
        ['Evening food', 'Wood Fired Pizzas from the Shack'],
        ['Day guests', '158'],
        ['Evening guests', '188'],
        ['Quotes run', '1'],
        ['First seen', '5 days ago'],
        ['Last active', '5 days ago'],
      ],
    },
    {
      id: 9,
      rank: 9,
      name: 'Milly Reilly',
      venue: 'The Great Barn',
      date: '2 date options',
      score: 56,
      band: 'hot',
      bandLabel: 'Hot',
      total: '£20,409',
      highValue: true,
      fitClass: 'unknown',
      fitGrade: 'Budget not given',
      reason: 'Estimated total £20,409, well above the £15k high value line; one quote so far, so reach out early',
      signals: [
        ['value', '★ High value'],
        ['grey', 'First quote'],
        ['grey', 'New, yesterday'],
      ],
      profile:
        'A £20,409 enquiry at The Great Barn with 2 dates in play. Their first quote, yesterday. No budget band was recorded, so they are ranked on value, recency and repeat visits. Numbers are 100 day and 200 evening.',
      gapLeft: ['Stated budget', 'Not given'],
      gapRight: ['Estimated total', '£20,409'],
      fitNote:
        'No budget band was recorded, so they are ranked on value, recency and repeat visits.',
      why: [
        'Estimated total £20,409, well above the £15k high value line',
        'Came in yesterday',
        'One quote so far, so reach out early',
        '2 dates still open, so undecided',
      ],
      action: 'Call today, hold the options, and book a viewing this week.',
      draftSubj: 'Your June 2029 wedding at The Great Barn',
      draft:
        'Hi Milly,\n\nThank you for building your quote with us. Your June 2029 wedding would be lovely at The Great Barn, and the dates you are considering are open.\n\nThe best next step is usually to come and see the space. I have viewing slots this week if any suit.\n\nWarm regards,\nThe Great Barn team',
      quote: [
        ['Venue', 'The Great Barn'],
        ['Date', '2 date options'],
        ['Breakfast menu', 'The Formal'],
        ['Drinks', 'Deluxe'],
        ['Evening food', 'Wood Fired Pizzas from the Shack'],
        ['Day guests', '100'],
        ['Evening guests', '200'],
        ['Quotes run', '1'],
        ['First seen', 'yesterday'],
        ['Last active', 'yesterday'],
      ],
    },
    {
      id: 10,
      rank: 10,
      name: 'Danielle Glover',
      venue: 'Ashton Lodge Country House',
      date: '2 date options',
      score: 56,
      band: 'hot',
      bandLabel: 'Hot',
      total: '£17,533',
      highValue: true,
      fitClass: 'top',
      fitGrade: 'Top bracket, no ceiling',
      reason: 'Estimated total £17,533, well above the £15k high value line; 2 quotes so far',
      signals: [
        ['value', '★ High value'],
        ['blue', 'Top bracket'],
        ['blue', 'Returning x2'],
        ['amber', 'Peak Saturday'],
      ],
      profile:
        'A £17,533 enquiry at Ashton Lodge Country House with 2 dates in play. Back 2 times, most recent 3 days ago. They are in the open top budget band, so there is no ceiling and they are judged on value. Numbers are 100 day and 150 evening.',
      gapLeft: ['Stated budget', '11,000+ GBP'],
      gapRight: ['Estimated total', '£17,533'],
      fitNote:
        'They are in the open top budget band, so there is no ceiling and they are judged on value.',
      why: [
        'Estimated total £17,533, well above the £15k high value line',
        'Last active 3 days ago, so fresh',
        '2 quotes so far',
        '2 dates still open, so undecided',
      ],
      action: 'Call today, hold the options, and book a viewing this week.',
      draftSubj: 'Your August 2028 wedding at Ashton Lodge Country House',
      draft:
        'Hi Danielle,\n\nThank you for building your quote with us. Your August 2028 wedding would be lovely at Ashton Lodge Country House, and the dates you are considering are open.\n\nThe best next step is usually to come and see the space. I have viewing slots this week if any suit.\n\nWarm regards,\nThe Ashton Lodge Country House team',
      quote: [
        ['Venue', 'Ashton Lodge Country House'],
        ['Date', '2 date options'],
        ['Breakfast menu', 'The Casual'],
        ['Drinks', 'Indulgence'],
        ['Evening food', 'Wood Fired Pizzas from the Shack'],
        ['Day guests', '100'],
        ['Evening guests', '150'],
        ['Quotes run', '2'],
        ['First seen', '26 days ago'],
        ['Last active', '3 days ago'],
      ],
    },
  ],
};
